import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { RedButton, GhostButton } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";

const STATE = {
  free: ["LIBRE", "border-emerald-600/60 bg-emerald-50 text-emerald-800"],
  pending: ["EN ATTENTE", "border-amber-500/70 bg-amber-50 text-amber-800"],
  booked: ["OCCUPÉ", "border-sared bg-sared/10 text-sared"],
  blocked: ["FERMÉ", "border-zinc-300 bg-zinc-100 text-zinc-500"],
  past: ["PASSÉ", "border-zinc-200 bg-zinc-50 text-zinc-400"],
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const frDate = (s) => new Date(s + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

export default function Planning() {
  const { settings } = useSite();
  const [weekStart, setWeekStart] = useState(new Date());
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoursText, setHoursText] = useState("");

  useEffect(() => {
    api.get("/planning/hours").then(({ data }) => setHoursText(data.text)).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/planning/days?start=${iso(weekStart)}&count=7`);
        if (active) setDays(data.days);
      } catch (e) { toast.error(apiError(e)); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [weekStart]);

  return (
    <div data-testid="planning-page">
      <PageHero overline="Disponibilités" title="Planning" />
      <section className="mx-auto max-w-[1400px] px-4 md:px-8 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="planning-prev"
                    className="border border-zinc-800 p-2 text-zinc-300 hover:border-sared"><ChevronLeft size={16} /></button>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="planning-next"
                    className="border border-zinc-800 p-2 text-zinc-300 hover:border-sared"><ChevronRight size={16} /></button>
            <span className="font-head text-xl text-white capitalize" data-testid="planning-range">
              {days.length ? `${frDate(days[0].date)} → ${frDate(days[days.length - 1].date)}` : ""}
            </span>
          </div>
          <div className="flex flex-wrap gap-4 font-mono-tech text-[9px] tracking-[0.16em] text-zinc-400">
            {Object.entries(STATE).filter(([k]) => k !== "past").map(([k, [label, cls]]) => (
              <span key={k} className="flex items-center gap-2"><span className={`inline-block h-3 w-3 border ${cls}`} />{label}</span>
            ))}
          </div>
        </div>

        {loading ? <p className="text-zinc-500">Chargement...</p> : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {days.map((d) => (
              <div key={d.date} className="border border-zinc-800 bg-[#0a0a0a] p-4" data-testid={`planning-day-${d.date}`}>
                <div className="font-head text-lg text-white capitalize">{frDate(d.date)}</div>
                {!d.open && <div className="mt-2 font-mono-tech text-[9px] tracking-[0.16em] text-zinc-500">{d.reason || "FERMÉ"}</div>}
                <div className="mt-3 grid gap-2">
                  {d.slots.map((x) => (
                    <div key={x.start} data-testid={`planning-slot-${d.date}-${x.start}`} data-state={x.state}
                         className={`border px-3 py-2 font-mono-tech text-[10px] tracking-[0.12em] whitespace-nowrap ${STATE[x.state]?.[1] || STATE.blocked[1]}`}>
                      {x.start} – {x.end} · {STATE[x.state]?.[0] || "FERMÉ"}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 border-l-2 border-sared pl-5">
          <p className="text-sm text-zinc-300 whitespace-pre-line" data-testid="planning-hours">{hoursText || settings?.hours}</p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link to="/rendez-vous"><RedButton>Réserver un créneau</RedButton></Link>
            <Link to="/mon-rendez-vous"><GhostButton>Gérer mon rendez-vous</GhostButton></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
